import { useCallback, useEffect, useRef, useState } from "react";
import QrScanner from "qr-scanner";

QrScanner.WORKER_PATH = `${
  process.env.PUBLIC_URL || ""
}/qr-scanner-worker.min.js`;

const CAMERA_ERROR_MESSAGE =
  "Unable to access the camera. Allow camera permission and reload this page.";

/** Bootstrap class for the scanner mount node — large viewfinder improves decode quality. */
export const QR_SCANNER_CONTAINER_CLASS =
  "w-100 overflow-hidden min-vh-50 position-relative bg-dark";

/**
 * Decode the full frame at high resolution — helps small printed QRs on stickers.
 * @see https://github.com/nimiq/qr-scanner#customizing-the-scan-region
 */
const fullFrameScanRegion = (video) => {
  const width = video.videoWidth;
  const height = video.videoHeight;

  return {
    x: 0,
    y: 0,
    width,
    height,
    downScaledWidth: Math.min(width, 1920),
    downScaledHeight: Math.min(height, 1920),
  };
};

export const useQrScanner = ({ elementId, enabled, onScan }) => {
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  const scannerRef = useRef(null);
  const videoRef = useRef(null);
  const [scannerReady, setScannerReady] = useState(false);
  const [scannerError, setScannerError] = useState(null);

  const clearScannerError = useCallback(() => {
    setScannerError(null);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setScannerReady(false);
      return undefined;
    }

    let isMounted = true;
    let frameId = 0;

    const fail = (message = CAMERA_ERROR_MESSAGE) => {
      if (!isMounted) return;
      setScannerReady(false);
      setScannerError(message);
    };

    const cleanup = () => {
      scannerRef.current?.destroy();
      scannerRef.current = null;
      videoRef.current?.remove();
      videoRef.current = null;
    };

    frameId = requestAnimationFrame(() => {
      if (!isMounted) return;

      const container = document.getElementById(elementId);
      if (!container) {
        fail("Unable to start camera scanner.");
        return;
      }

      container.replaceChildren();

      const video = document.createElement("video");
      video.playsInline = true;
      video.muted = true;
      video.setAttribute("playsinline", "true");
      video.className = "w-100 h-100 object-fit-cover d-block";
      container.appendChild(video);
      videoRef.current = video;

      const scanner = new QrScanner(
        video,
        (result) => {
          const text = typeof result === "string" ? result : result?.data ?? "";
          if (text) onScanRef.current?.(text);
        },
        {
          preferredCamera: "environment",
          highlightScanRegion: true,
          highlightCodeOutline: true,
          maxScansPerSecond: 8,
          calculateScanRegion: fullFrameScanRegion,
        }
      );

      scannerRef.current = scanner;

      scanner
        .start()
        .then(() => {
          if (!isMounted) {
            cleanup();
            return;
          }
          setScannerError(null);
          setScannerReady(true);
        })
        .catch((err) => {
          cleanup();
          if (!isMounted) return;

          const name = err?.name ?? "";
          if (name === "NotAllowedError" || name === "PermissionDeniedError") {
            fail(CAMERA_ERROR_MESSAGE);
            return;
          }

          fail(CAMERA_ERROR_MESSAGE);
        });
    });

    return () => {
      isMounted = false;
      cancelAnimationFrame(frameId);
      setScannerReady(false);
      cleanup();
    };
  }, [elementId, enabled]);

  return { scannerReady, scannerError, clearScannerError };
};
